import { EPSILON } from './geometry.js';
import type { DrawPoint, Vec } from './types.js';

function dist2(ax: number, ay: number, bx: number, by: number): number {
  const dx = ax - bx;
  const dy = ay - by;
  return dx * dx + dy * dy;
}

function mix(a: DrawPoint, b: DrawPoint, t: number): DrawPoint {
  const u = t < 0 ? 0 : t > 1 ? 1 : t;
  return [a[0] + (b[0] - a[0]) * u, a[1] + (b[1] - a[1]) * u, a[2] + (b[2] - a[2]) * u];
}

/**
 * Parameter values where segment a→b crosses the circle around `center`.
 * Returns 0..2 values of t in (0, 1), sorted.
 */
function circleHits(a: DrawPoint, b: DrawPoint, center: Vec, radius: number): number[] {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const fx = a[0] - center.x;
  const fy = a[1] - center.y;
  const A = dx * dx + dy * dy;
  if (A < EPSILON) return [];
  const B = 2 * (fx * dx + fy * dy);
  const C = fx * fx + fy * fy - radius * radius;
  const disc = B * B - 4 * A * C;
  if (disc < 0) return [];
  const s = Math.sqrt(disc);
  const out: number[] = [];
  for (const t of [(-B - s) / (2 * A), (-B + s) / (2 * A)]) {
    if (t > 0.001 && t < 0.999) out.push(t);
  }
  return out.sort((x, y) => x - y);
}

/**
 * Cuts a freehand stroke where it intersects an eraser circle.
 * Returns zero or more remaining polylines in the same coordinate space.
 */
export function eraseStrokeAt(
  points: readonly DrawPoint[],
  center: Vec,
  radius: number,
): DrawPoint[][] {
  if (points.length === 0) return [];
  if (radius <= 0) return [points.slice()];

  const r2 = radius * radius;
  const outside = (p: DrawPoint) => dist2(p[0], p[1], center.x, center.y) > r2;

  if (points.length === 1) {
    return outside(points[0]!) ? [points.slice()] : [];
  }

  const runs: DrawPoint[][] = [];
  let run: DrawPoint[] = [];

  const push = (p: DrawPoint) => {
    const last = run[run.length - 1];
    if (last && Math.abs(last[0] - p[0]) < EPSILON && Math.abs(last[1] - p[1]) < EPSILON) {
      return;
    }
    run.push(p);
  };

  const flush = () => {
    if (run.length >= 2) runs.push(run);
    run = [];
  };

  for (let i = 0; i < points.length; i++) {
    const p = points[i]!;
    const pOut = outside(p);

    if (i > 0) {
      const prev = points[i - 1]!;
      const prevOut = outside(prev);
      if (prevOut !== pOut) {
        const hits = circleHits(prev, p, center, radius);
        const hit = hits.length > 0 ? mix(prev, p, hits[0]!) : null;
        if (prevOut) {
          if (hit) push(hit);
          flush();
        } else {
          flush();
          if (hit) push(hit);
        }
      } else if (prevOut && pOut) {
        // Both ends outside but the segment itself passes through the eraser:
        // a long, simplified straight run would otherwise survive a sweep
        // straight across its middle. Cut out the chord between the two hits.
        const hits = circleHits(prev, p, center, radius);
        if (hits.length === 2) {
          push(mix(prev, p, hits[0]!));
          flush();
          push(mix(prev, p, hits[1]!));
        }
      } else if (!prevOut && !pOut) {
        // Entire segment inside the eraser — keep the run closed.
        flush();
      }
    }

    if (pOut) push(p);
    else flush();
  }

  flush();
  return runs;
}
